import { nonnull } from "@tento-core/assert";
import { DateTime, Utc } from "@tento-chrono";
import { Tracing } from "@tento-core/tracing";
import { ColumnIndex } from "@tento-ui/components/sheet/column-index";
import { SheetCell } from "@tento-ui/components/sheet/sheet-cell";
import { type SheetRow, SheetViewModel } from "@tento-lona";
import { CellGroup } from "@tento-ui/components/sheet/cell-group";
import {
  type ModelRow,
  type Option,
  type Role,
  type Timescale,
} from "@tento-lona/sheets";
import {
  RenderBinding,
  type RenderPluginLookup,
  type RenderPluginSpec,
  AliasRow,
  RendererRow,
  SourceRow,
} from "@tento-lona/sheets/platform";
import { type BindablePlugin, bindableLookup } from "../render-plugin.ts";
import { SheetDelegate } from "../sheet-delegate.ts";
import { rowIdentity } from "@tento-ui/components/sheet/row-identity";
import { liveVariantOf } from "../live-variant.ts";
import type { SheetBinder } from "../sheet-index-renderer.ts";

export const CELL_BINDING_PLUGIN_ATTR = "data-cell-binding-plugin";
export const CELL_BINDING_PLUGIN_ROLE_ATTR = "data-cell-binding-plugin-role";

export interface CellBinding {
  plugin: BindablePlugin;
  pluginId: string;
  row: unknown;
  renderRow?: SheetRow.Joined.Handle;
  contentType: string;
  binding: RenderBinding;
}

function bindCellPluginAttributes(
  $cell: HTMLElement,
  binding: Pick<RenderBinding, "pluginId" | "pluginRole">,
): void {
  $cell.setAttribute(CELL_BINDING_PLUGIN_ATTR, binding.pluginId);
  $cell.setAttribute(CELL_BINDING_PLUGIN_ROLE_ATTR, binding.pluginRole);
}

function parseCellBindingPluginRole(
  value: Option<string>,
): Option<Role> {
  switch (value) {
    case "source":
    case "renderer":
    case "layout":
    case "alias":
      return value;
    default:
      return null;
  }
}

/**
 * Generate sub-columns from a parent column for stacked cell binding.
 * Uses the parent PartialDate's helper to generate constituent sub-partials.
 *
 * @param parentColumn - The parent column in the sheet's timescale
 * @param rowTimescale - The row's timescale (day, week, month, or year - NOT custom)
 * @param count - Number of sub-columns to generate
 * @returns Array of sub-columns as ColumnIndex.Key.Resolved.Default
 */
function generateSubColumns(
  parentColumn: ColumnIndex.Key.Resolved.Default,
  rowTimescale: Timescale,
  count: number,
): ColumnIndex.Key.Resolved.Default[] {
  const tz = parentColumn.tz;
  const baseOffset = parentColumn.offset;
  const subPartials = parentColumn.pd.generateSubPartials(rowTimescale, count);
  return subPartials.map(
    (pd) =>
      new ColumnIndex.Key.Resolved.Default(pd, tz, baseOffset, null, null),
  );
}

/**
 * Represents the temporal state of a column relative to the current time.
 */
export interface ColumnTimeState {
  inThePast: boolean;
  isCurrent: boolean;
  isFuture: boolean;
}

/**
 * Compute whether a column is in the past, current, or future relative to now.
 * This is pure computation logic extracted for testability.
 */
function computeColumnTimeState(
  now: DateTime<Utc>,
  columnRange: DateTime.Range<Utc>,
): ColumnTimeState {
  const nowBeforeStart = now.isBefore(columnRange.start);
  const containsNow = columnRange.containsExclusiveEnd(now);
  // inThePast means the column is in the past (now is AFTER the column)
  // So inThePast = !nowBeforeStart && !containsNow
  const inThePast = !nowBeforeStart && !containsNow;
  const isCurrent = containsNow;
  const isFuture = nowBeforeStart;
  return {
    inThePast,
    isCurrent,
    isFuture,
  };
}

/**
 * Determine if subcolumns should be generated for stacking.
 * Subcolumns are only generated when:
 * 1. Layout has stacking info
 * 2. Row timescale is not "custom"
 */
function shouldGenerateSubcolumns(
  layout: Option<SheetCell.Layout>,
  timescaleSetting: Timescale | "custom",
): boolean {
  return layout?.stackingInfo != null && timescaleSetting !== "custom";
}

/**
 * Cache for subcolumn generation to avoid redundant partial date calculations.
 * Subcolumns are expensive to generate and often reused across rebinds.
 */
export class SubcolumnCache {
  #cache = new Map<string, ColumnIndex.Key.Resolved.Default[]>();

  /**
   * Get cached subcolumns or generate and cache them.
   */
  getOrGenerate(
    parentColumn: ColumnIndex.Key.Resolved.Default,
    timescaleSetting: Timescale,
    count: number,
  ): ColumnIndex.Key.Resolved.Default[] {
    // Use the PartialDate's string representation as the cache key
    // This uniquely identifies the actual date range, not just the visual offset
    const key = `${parentColumn.pd.toString()}-${timescaleSetting}-${count}`;

    const cached = this.#cache.get(key);
    if (cached) {
      return cached;
    }
    const result = generateSubColumns(parentColumn, timescaleSetting, count);
    this.#cache.set(key, result);
    return result;
  }

  /**
   * Clear the cache when column configuration changes.
   */
  clear(): void {
    this.#cache.clear();
  }

  /**
   * Get current cache size for debugging/telemetry.
   */
  size(): number {
    return this.#cache.size;
  }
}

/**
 * Builder for creating cell content (normal or stacked).
 * Extracted from DefaultBinder to reduce complexity and improve testability.
 */
export class CellContentBuilder {
  /**
   * Build normal (non-stacked) cell content.
   * Simply creates and returns the plugin cell.
   */
  buildNormalContent(
    row: SheetRow.Joined.Handle,
    pluginManager: SheetDelegate.PluginManager,
  ): HTMLElement {
    return resolveCellBinding(row, pluginManager).plugin.makeCell();
  }

  /**
   * Build stacked cell content with multiple subcells wrapped in a CellGroup.
   */
  buildStackedContent(
    row: SheetRow.Joined.Handle,
    pluginManager: SheetDelegate.PluginManager,
    subColumns: ColumnIndex.Key.Resolved.Default[],
    layout: SheetCell.Layout,
    now: DateTime<Utc>,
    onFocusCell: Option<(id: SheetCell.CellId) => void>,
    onBlurCell: Option<(id: SheetCell.CellId) => void>,
  ): CellGroup {
    const {
      cellsPerColumn,
      individualCellHeight,
      visibleCellCount,
      seeMoreHeight,
    } = nonnull(layout.stackingInfo, "a stacked cell's layout carries its stacking info");

    const binding = resolveCellBinding(row, pluginManager);

    // Create SheetCell wrappers for each plugin cell
    const $sheetCells: SheetCell[] = [];
    for (const sc of subColumns) {
      const $pluginCell = binding.plugin.makeCell();
      const $subcell = this.#createSubcell(
        row,
        sc,
        $pluginCell,
        binding.contentType,
        now,
        onFocusCell,
        onBlurCell,
      );
      bindCellPluginAttributes($subcell, binding.binding);
      $sheetCells.push($subcell);
    }

    // Create and configure CellGroup
    const $cellGroup = CellGroup.make();
    $cellGroup.bind($sheetCells, {
      individualCellHeight,
      visibleCellCount,
      seeMoreHeight,
      totalCells: cellsPerColumn,
    });

    return $cellGroup;
  }

  /**
   * Create a subcell with proper configuration for stacked mode.
   */
  #createSubcell(
    row: SheetRow.Joined.Handle,
    column: ColumnIndex.Key.Resolved.Default,
    $pluginCell: HTMLElement,
    contentType: string,
    now: DateTime<Utc>,
    onFocusCell: Option<(id: SheetCell.CellId) => void>,
    onBlurCell: Option<(id: SheetCell.CellId) => void>,
  ): SheetCell {
    const { inThePast, isCurrent } = computeColumnTimeState(now, column.range);
    const $subcell = SheetCell.make() as SheetCell;
    $subcell.setId({
      row: rowIdentity(row.id.toString()),
      col: column,
    });
    $subcell.onFocusCell = onFocusCell;
    $subcell.onBlurCell = onBlurCell;
    // CellGroup needs these flags for its initial labels. applyPlanFull follows
    // construction with bindPluginCells, which applies all layout options once.
    $subcell.toggleAttribute("in-the-past", inThePast);
    $subcell.toggleAttribute("is-current", isCurrent);
    $subcell.bindContent(contentType, $pluginCell);
    return $subcell;
  }
}

/**
 * Context needed for binding plugin cells.
 */
export interface PluginBindContext {
  sheet: SheetViewModel.Surface<SheetBinder>;
  row: SheetRow.Joined.Handle;
  widthPx: number;
  inThePast: boolean;
  isCurrent: boolean;
  handle: SheetDelegate;
  now: DateTime<Utc>;
}

/**
 * Bind all plugin cells within a SheetCell.
 * Handles both normal (single cell) and stacked (multiple cells) modes.
 * Eliminates triply-duplicated plugin binding logic from onBind/onResize/onRebind.
 */
function bindPluginCells(
  $cell: SheetCell,
  baseColumn: ColumnIndex.Key.Resolved.Default,
  subColumns: Option<ColumnIndex.Key.Resolved.Default[]>,
  context: PluginBindContext,
): void {
  const pluginCells = $cell.getPluginCells();

  // Update subcell IDs and layout options when in stacked mode (for recycled cells)
  if (subColumns && $cell.$content instanceof CellGroup) {
    const subcells = $cell.$content.getSubcells();
    for (const [i, subcell] of subcells.entries()) {
      const col = subColumns[i];

      // If there's no corresponding subColumn, hide the subcell entirely: a
      // subcell with no column has no cell id.
      if (!col) {
        subcell.cellId = null;
        subcell.style.display = "none";
        continue;
      }

      // Ensure subcell is visible (may have been hidden previously)
      subcell.style.display = "";
      subcell.setId({
        row: rowIdentity(context.row.id.toString()),
        col,
      });
      // Update layout options with correct time state for this subcell
      const { inThePast, isCurrent } = computeColumnTimeState(
        context.now,
        col.range,
      );
      const { background: _, ...rowOptionsWithoutBackground } =
        context.row.cellLayoutOptions ?? {};
      subcell.bindLayoutOptions(
        {
          card: true,
          canDrawCardBorder: false,
          marginInline: "0px",
          marginBlock: "0px",
          ...rowOptionsWithoutBackground,
        },
        inThePast,
        isCurrent,
      );
    }
    // Refresh labels to show updated dates
    $cell.$content.rebindLabels();
  }

  // Pre-compute current date in base column's timezone (shared by all
  // non-stacked cells). Avoids repeated toTz per cell.
  const baseNow = baseColumn.tz.toTz(context.now).ndt.date;

  for (const [i, $pluginCell] of pluginCells.entries()) {
    const column = subColumns ? subColumns[i] : baseColumn;
    if (!column) continue;

    // For stacked cells, compute individual time state; otherwise use parent's
    const { inThePast, isCurrent } = subColumns
      ? computeColumnTimeState(context.now, column.range)
      : { inThePast: context.inThePast, isCurrent: context.isCurrent };

    const now = subColumns ? column.tz.toTz(context.now).ndt.date : baseNow;

    const binding = resolveCellBinding(
      context.row,
      context.handle.pluginManager,
    );
    const sheetRow = fixedChildrenRenderRow(context.row, binding);
    // Dispatch takes advantage of the runtime type invariant (the
    // resolved `row` matches plugin.rowClass / plugin.rendererClass
    // when declared, else is the Joined.Handle). TypeScript can't see
    // the pairing so we cast through the shared base.
    binding.plugin.bindCell($pluginCell, {
      sheet: context.sheet,
      row: binding.row,
      sheetRow,
      column,
      widthPx: context.widthPx,
      inThePast,
      isCurrent,
      handle: context.handle,
      now,
    });
  }
}

/**
 * Resize all plugin cells within a SheetCell.
 * Similar to bindPluginCells but calls onResize instead of bindCell.
 */
function resizePluginCells(
  $cell: SheetCell,
  baseColumn: ColumnIndex.Key.Resolved.Default,
  subColumns: Option<ColumnIndex.Key.Resolved.Default[]>,
  context: PluginBindContext,
): void {
  const pluginCells = $cell.getPluginCells();

  // Update subcell IDs and layout options when in stacked mode (for recycled cells)
  // This ensures labels are updated when scrolling to a new column
  if (subColumns && $cell.$content instanceof CellGroup) {
    const subcells = $cell.$content.getSubcells();
    for (const [i, subcell] of subcells.entries()) {
      const col = subColumns[i];

      // If there's no corresponding subColumn, hide the subcell entirely: a
      // subcell with no column has no cell id.
      if (!col) {
        subcell.cellId = null;
        subcell.style.display = "none";
        continue;
      }

      // Ensure subcell is visible (may have been hidden previously)
      subcell.style.display = "";
      subcell.setId({
        row: rowIdentity(context.row.id.toString()),
        col,
      });
      // Update layout options with correct time state for this subcell
      const { inThePast, isCurrent } = computeColumnTimeState(
        context.now,
        col.range,
      );
      const { background: _, ...rowOptionsWithoutBackground } =
        context.row.cellLayoutOptions ?? {};
      subcell.bindLayoutOptions(
        {
          card: true,
          canDrawCardBorder: false,
          marginInline: "0px",
          marginBlock: "0px",
          ...rowOptionsWithoutBackground,
        },
        inThePast,
        isCurrent,
      );
    }
    // Refresh labels to show updated dates
    $cell.$content.rebindLabels();
  }

  const baseNow = baseColumn.tz.toTz(context.now).ndt.date;

  for (const [i, $pluginCell] of pluginCells.entries()) {
    const column = subColumns ? subColumns[i] : baseColumn;
    if (!column) continue;

    const binding = resolveCellBinding(
      context.row,
      context.handle.pluginManager,
    );
    const sheetRow = fixedChildrenRenderRow(context.row, binding);
    binding.plugin.onResize($pluginCell, {
      sheet: context.sheet,
      row: binding.row,
      sheetRow,
      column,
      widthPx: context.widthPx,
      inThePast: context.inThePast,
      isCurrent: context.isCurrent,
      handle: context.handle,
      now: subColumns ? column.tz.toTz(context.now).ndt.date : baseNow,
    });
  }
}

/**
 * Resolve the actual cell binding for a visible row. Source rows do
 * not paint directly: they bind through an attached renderer row when
 * one exists, otherwise through the generic data-json viewer if
 * registered, finally through the canonical data JSON renderer.
 */
function resolveCellBinding(
  row: SheetRow.Joined.Handle,
  pluginManager: SheetDelegate.PluginManager,
): CellBinding {
  const variant = liveVariantOf(row);
  if (variant) {
    const binding = RenderBinding.resolveCell(
      variant,
      renderPluginLookup(pluginManager),
    );
    const plugin = bindableLookup(pluginManager).plugin(binding.pluginId, binding.pluginRole);
    const pluginVariant =
      findVariantById(variant, binding.pluginRowId) ?? variant;
    const renderer = isRendererVariant(pluginVariant)
      ? (pluginVariant as RendererRow)
      : null;
    const renderRow = renderRowForBinding(row, binding);
    return {
      plugin,
      pluginId: binding.pluginId,
      row: resolvePluginRow(plugin, renderRow ?? row, renderer),
      ...(renderRow ? { renderRow } : {}),
      contentType: binding.contentType,
      binding,
    };
  }

  const plugin = resolveLegacyCellPlugin(row, pluginManager);
  const binding = legacyCellBinding(row, plugin);
  return {
    plugin,
    pluginId: plugin.id,
    row: resolvePluginRow(plugin, row, null),
    contentType: binding.contentType,
    binding,
  };
}

function fixedChildrenRenderRow(
  row: SheetRow.Joined.Handle,
  binding: CellBinding,
): SheetRow.Joined.Handle {
  return binding.renderRow ?? row;
}

/**
 * Resolve plugins that may own row-level capabilities such as content
 * providers. Source-row capabilities live on the source plugin even
 * when cell painting is delegated to a renderer; alias capabilities
 * live on the alias plugin. The resolved cell plugin is still included
 * for renderer/layout-owned capabilities.
 */
function resolveRowCapabilityPlugins(
  row: SheetRow.Joined.Handle,
  pluginManager: SheetDelegate.PluginManager,
): readonly BindablePlugin[] {
  const plugins: BindablePlugin[] = [];
  const push = (plugin: BindablePlugin) => {
    if (plugins.includes(plugin)) return;
    plugins.push(plugin);
  };

  // Most specific first: a source plugin self-registered under the
  // row's WIRE TYPE (e.g. the scheduling-link plugin, id
  // "scheduling-link" role "source"). The rows-side render binding
  // keys source descriptors by shape ("source.obj…") and its
  // attribute heuristics can misclassify (a scheduling-link row's
  // `timezone` attribute reads as a timezone row), so the explicit
  // type registration outranks binding-derived resolution. Generic:
  // any plugin registered under a wire type qualifies; no
  // per-product cases here.
  if (isSourceVariant(liveVariantOf(row))) {
    const typePlugin = maybePluginForRole(
      pluginManager,
      row.descriptor.id,
      "source",
    );
    if (typePlugin) push(typePlugin);
  }

  if (isAliasVariant(liveVariantOf(row))) {
    push(resolveOwnPlugin(row, pluginManager));
    try {
      push(resolveCellBinding(row, pluginManager).plugin);
    } catch (error) {
      if (!RenderBinding.isNotCellBindable(error)) throw error;
    }
    return plugins;
  }

  // Binding-derived resolution is a boundary: heuristic hints can
  // pick a plugin whose binding requirements the row doesn't meet
  // (and throw). A failed derivation must not discard the explicit
  // type-registered plugins above — warn and return what resolved.
  try {
    const rowPlugin = resolveOwnPlugin(row, pluginManager);
    if (isSourceVariant(liveVariantOf(row)) && rowPlugin.role === "source") {
      push(rowPlugin);
    }
    push(resolveCellBinding(row, pluginManager).plugin);
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    Tracing.warn("ui")(
      `resolveRowCapabilityPlugins: binding resolution failed for row ` +
        `${String(row.id)} (${row.descriptor.id}) — ${message}`,
    );
  }
  return plugins;
}

/**
 * `resolveCellBinding`, but returns `null` for the recoverable
 * `RenderBinding.NotCellBindableError` instead of throwing — an alias whose
 * cell-bindable descendant is transiently un-materialized during a
 * churn-driven re-expansion, or an alias plugin asked to paint a cell. The
 * same condition `resolveRowCapabilityPlugins` swallows above. A mid-rebuild
 * alias momentarily violates the `flattened.rows` "plugin can bind"
 * invariant; the per-row render hooks must skip it that pass — not turn a
 * transient contract assertion into an uncaught throw that breaks scroll
 * rebinding. The next rebind binds it.
 */
function resolveCellBindingOrSkip(
  row: SheetRow.Joined.Handle,
  pluginManager: SheetDelegate.PluginManager,
): Option<CellBinding> {
  try {
    return resolveCellBinding(row, pluginManager);
  } catch (error) {
    if (RenderBinding.isNotCellBindable(error)) return null;
    throw error;
  }
}

function findJoinedRowById(
  row: SheetRow.Joined.Handle,
  id: RenderBinding["pluginRowId"],
): Option<SheetRow.Joined.Handle> {
  if (sameRowId(row.id, id)) return row;
  for (const child of row.children()) {
    const found = findJoinedRowById(child, id);
    if (found) return found;
  }
  return null;
}

function findVariantById(
  row: ModelRow,
  id: RenderBinding["pluginRowId"],
): Option<ModelRow> {
  if (sameRowId(row.id, id)) return row;
  for (const child of row.children) {
    const found = findVariantById(child, id);
    if (found) return found;
  }
  return null;
}

function renderRowForBinding(
  row: SheetRow.Joined.Handle,
  binding: RenderBinding,
): Option<SheetRow.Joined.Handle> {
  if (binding.sourceRowId) {
    const sourceRow = findJoinedRowById(row, binding.sourceRowId);
    if (sourceRow) return sourceRow;
  }
  const pluginRow = findJoinedRowById(row, binding.pluginRowId);
  if (pluginRow && !sameRowId(pluginRow.id, row.id)) return pluginRow;
  return null;
}

function sameRowId(
  left: { toString(): string },
  right: { toString(): string },
): boolean {
  return left.toString() === right.toString();
}

function legacyCellBinding(
  row: SheetRow.Joined.Handle,
  plugin: BindablePlugin,
): RenderBinding {
  const role = plugin.role;
  return {
    surface: "cell",
    requestedRowId: row.id,
    pluginRowId: row.id,
    sourceRowId: role === "source" ? row.id : null,
    pluginId: plugin.id,
    pluginRole: role,
    rowRole: role,
    contentType: row.descriptor.id,
    bindingKind: "row-descriptor",
    boundRowId: null,
    boundRendererId: null,
  };
}

function resolveOwnPlugin(
  row: SheetRow.Joined.Handle,
  pluginManager: SheetDelegate.PluginManager,
): BindablePlugin {
  const variant = liveVariantOf(row);
  if (variant) {
    const binding = RenderBinding.resolveRow(
      variant,
      renderPluginLookup(pluginManager),
    );
    return bindableLookup(pluginManager).plugin(binding.pluginId, binding.pluginRole);
  }
  return resolveLegacyOwnPlugin(row, pluginManager);
}

function resolveLegacyCellPlugin(
  row: SheetRow.Joined.Handle,
  pluginManager: SheetDelegate.PluginManager,
): BindablePlugin {
  const plugin =
    maybePluginForRole(pluginManager, row.descriptor.id, "layout") ??
    maybePluginForRole(pluginManager, row.descriptor.id, "renderer") ??
    maybePluginForRole(pluginManager, row.descriptor.id, "source");
  if (plugin) return plugin;
  return bindableLookup(pluginManager).cellPlugin(row.descriptor.id);
}

function resolveLegacyOwnPlugin(
  row: SheetRow.Joined.Handle,
  pluginManager: SheetDelegate.PluginManager,
): BindablePlugin {
  const plugin =
    maybePluginForRole(pluginManager, row.descriptor.id, "alias") ??
    maybePluginForRole(pluginManager, row.descriptor.id, "layout") ??
    maybePluginForRole(pluginManager, row.descriptor.id, "renderer") ??
    maybePluginForRole(pluginManager, row.descriptor.id, "source");
  if (plugin) return plugin;
  return bindableLookup(pluginManager).plugin(row.descriptor.id, "renderer");
}

function maybePluginForRole(
  pluginManager: SheetDelegate.PluginManager,
  type: string,
  role: Role,
): Option<BindablePlugin> {
  return bindableLookup(pluginManager).registered(type, role);
}

function renderPluginLookup(
  pluginManager: SheetDelegate.PluginManager,
): RenderPluginLookup {
  return {
    get(id: string, role: Role): RenderPluginSpec {
      const plugin = pluginManager.get(id, role);
      return {
        id: plugin.id,
        role: plugin.role,
        ...(plugin.aliases !== undefined ? { aliases: plugin.aliases } : {}),
      };
    },
  };
}

/**
 * When a plugin declares `rowClass` (or legacy `rendererClass`),
 * resolve the rows-side renderer subclass attached as the source's
 * default-renderer child. Throws when the pairing is missing — the
 * infra guarantees it. Plugins without either keep the lona-js
 * `Joined.Handle` as their `row` (unchanged behaviour from the
 * pre-renderer world).
 */
function resolvePluginRow(
  plugin: {
    id: string;
    role: Role;
    rowClass?: new (...args: never[]) => unknown;
    rendererClass?: new (...args: never[]) => unknown;
  },
  row: SheetRow.Joined.Handle,
  resolvedRenderer: Option<RendererRow>,
): unknown {
  const ctorCandidate = plugin.rowClass ?? plugin.rendererClass;
  if (!ctorCandidate) return row;
  if (
    resolvedRenderer instanceof ctorCandidate ||
    (isRendererVariant(resolvedRenderer) &&
      resolvedRenderer.descriptor.rendererId === plugin.id)
  )
    return resolvedRenderer;
  const source = liveVariantOf(row);
  if (!isSourceVariant(source)) {
    throw new Error(
      `${plugin.id}: expected SourceRow for row ${row.id} ` +
        `(type=${row.descriptor.id}), got ${source?.constructor.name ?? "null"}. ` +
        `Plugin declares rowClass / rendererClass — a rows-side Sheet ` +
        `must be attached.`,
    );
  }
  const renderer =
    (isRendererClass(ctorCandidate)
      ? (source as SourceRow).defaultRenderer(ctorCandidate)
      : null) ??
    firstRendererChildForPlugin(source as SourceRow, plugin.id);
  if (renderer === null) {
    throw new Error(
      `${plugin.id}: row ${row.id} has no ${ctorCandidate.name} ` +
        `default renderer child — check the row type's childSpec ` +
        `registration in row-type-specs-builtin.ts.`,
    );
  }
  return renderer;
}

function isSourceVariant(
  variant: Option<{ descriptor?: { role?: string } }>,
): variant is SourceRow {
  return variant instanceof SourceRow || variant?.descriptor?.role === "source";
}

/** Whether a plugin's declared row class is a `RendererRow` subclass. */
function isRendererClass(
  ctor: new (...args: never[]) => unknown,
): ctor is new (...args: never[]) => RendererRow {
  return ctor === RendererRow || ctor.prototype instanceof RendererRow;
}

function isRendererVariant(
  variant: Option<{ descriptor?: { role?: string } }>,
): variant is RendererRow {
  return (
    variant instanceof RendererRow || variant?.descriptor?.role === "renderer"
  );
}

function isAliasVariant(
  variant: Option<{ descriptor?: { role?: string } }>,
): variant is AliasRow {
  return variant instanceof AliasRow || variant?.descriptor?.role === "alias";
}

function firstRendererChildForPlugin(
  source: SourceRow,
  pluginId: string,
): Option<RendererRow> {
  for (const child of source.children) {
    if (!isRendererVariant(child)) continue;
    const renderer = child as RendererRow;
    if (renderer.descriptor.rendererId === pluginId) return renderer;
  }
  return null;
}

// ── DefaultBinder namespace ──────────────────────────────────────

const $bindCellPluginAttributes = bindCellPluginAttributes;
const $parseCellBindingPluginRole = parseCellBindingPluginRole;
const $computeColumnTimeState = computeColumnTimeState;
const $shouldGenerateSubcolumns = shouldGenerateSubcolumns;
const $bindPluginCells = bindPluginCells;
const $resizePluginCells = resizePluginCells;
const $resolveCellBinding = resolveCellBinding;
const $resolveCellBindingOrSkip = resolveCellBindingOrSkip;
const $fixedChildrenRenderRow = fixedChildrenRenderRow;
const $resolveRowCapabilityPlugins = resolveRowCapabilityPlugins;
const $resolveLegacyCellPlugin = resolveLegacyCellPlugin;

export namespace DefaultBinderHelpers {
  export const bindCellPluginAttributes = $bindCellPluginAttributes;
  export const parseCellBindingPluginRole = $parseCellBindingPluginRole;
  export const computeColumnTimeState = $computeColumnTimeState;
  export const shouldGenerateSubcolumns = $shouldGenerateSubcolumns;
  export const bindPluginCells = $bindPluginCells;
  export const resizePluginCells = $resizePluginCells;
  export const resolveCellBinding = $resolveCellBinding;
  export const resolveCellBindingOrSkip = $resolveCellBindingOrSkip;
  export const fixedChildrenRenderRow = $fixedChildrenRenderRow;
  export const resolveRowCapabilityPlugins = $resolveRowCapabilityPlugins;
  export const resolveLegacyCellPlugin = $resolveLegacyCellPlugin;
}
